<template>
  <div class="pb-8">

    <!-- Loading state -->
    <div v-if="isLoading" class="flex justify-center items-center py-16">
      <scale-loader color="#5580b5"></scale-loader>
    </div>

    <div v-else class="flex gap-4 items-start">

      <!-- Left column: working days + holidays list -->
      <div class="flex flex-col gap-4" style="width:280px;flex-shrink:0">

        <!-- Working days config panel -->
        <div class="bg-white border border-gray-200 rounded-xl px-5 py-4 shadow-sm">
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center gap-2">
              <b-icon pack="fas" icon="briefcase" type="is-primary" size="is-small"></b-icon>
              <span class="font-bold text-sm tracking-wide" style="color:#3d5a8a">{{ $t('working_days') }}</span>
            </div>
          </div>

          <div class="flex flex-col gap-2">
            <label v-for="day in weekDays" :key="day.value"
              class="day-label flex items-center gap-3 px-3 py-2 rounded-lg border-2 cursor-pointer select-none transition-all duration-150"
              :class="{ 'is-active': workingDays.includes(day.value) }"
              @click="toggleDay(day.value)">
              <span class="text-xs font-bold uppercase tracking-wider w-6 text-center">{{ day.abbr }}</span>
              <span class="text-xs opacity-80 flex-1">{{ day.label }}</span>
              <b-icon v-if="workingDays.includes(day.value)" pack="fas" icon="check" size="is-small"></b-icon>
            </label>
          </div>

          <p class="flex items-center gap-1 text-xs text-gray-400 mt-3">
            <b-icon pack="fas" icon="info-circle" size="is-small"></b-icon>
            {{ $t('working_days_hint') }}
          </p>

          <b-button type="is-primary" size="is-small" icon-pack="fas" icon-left="save"
            :loading="savingWorkingDays" rounded expanded class="mt-3" @click="saveWorkingDays">
            {{ $t('save') }}
          </b-button>
        </div>

        <!-- Holidays list -->
        <div v-if="holidays.length" class="bg-white border border-gray-200 rounded-xl px-5 py-4 shadow-sm">
          <div class="flex items-center gap-1 font-bold text-sm text-red-700 uppercase tracking-wider mb-3">
            <b-icon pack="fas" icon="calendar-times" type="is-danger" size="is-small"></b-icon>
            {{ $t('holidays_list') }}
          </div>
          <div class="flex flex-col gap-2">
            <div v-for="h in holidaysSorted" :key="h.id"
              class="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5 text-sm">
              <span class="w-2 h-2 rounded-full bg-red-400 flex-shrink-0"></span>
              <div class="flex flex-col flex-1 min-w-0">
                <span class="text-xs text-red-500 font-semibold">{{ formatDate(h.date) }}</span>
                <span class="text-gray-700 text-xs truncate">{{ h.name }}</span>
              </div>
              <b-button type="is-danger" size="is-small" icon-pack="fas" icon-left="trash"
                outlined rounded @click="deleteHolidayFromList(h)"></b-button>
            </div>
          </div>
        </div>

      </div>

      <!-- Right column: calendar -->
      <div class="flex-1 min-w-0">
        <div class="bg-white border border-gray-200 rounded-xl px-5 py-4 shadow-sm">

          <!-- Legend & info bar -->
          <div class="flex items-center flex-wrap gap-4 mb-3">
            <div class="flex gap-4">
              <span class="flex items-center gap-1 text-xs text-gray-500">
                <span class="inline-block w-3.5 h-3.5 rounded flex-shrink-0 bg-orange-50 border border-orange-300"></span>
                {{ $t('non_working_day') }}
              </span>
              <span class="flex items-center gap-1 text-xs text-gray-500">
                <span class="inline-block w-3.5 h-3.5 rounded flex-shrink-0 bg-red-400"></span>
                {{ $t('holiday') }}
              </span>
            </div>
            <div class="flex items-center gap-1 text-xs text-gray-400 italic">
              <b-icon pack="fas" icon="mouse-pointer" size="is-small" type="is-info"></b-icon>
              {{ $t('click_day_to_add_holiday') }}
            </div>
            <div v-if="holidays.length"
              class="ml-auto flex items-center gap-1 text-xs font-semibold text-red-500 bg-red-50 border border-red-200 rounded-full px-3 py-0.5">
              <b-icon pack="fas" icon="calendar-times" size="is-small"></b-icon>
              {{ holidays.length }} {{ $t('holidays_configured') }}
            </div>
          </div>

          <div class="rounded-lg overflow-hidden">
            <FullCalendar ref="fullCalendar" :options="calendarOptions" />
          </div>

          <div v-if="!isLoading && (!projectStartDate || !projectEndDate)"
            class="flex items-center gap-1 mt-2 text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-3 py-1.5">
            <b-icon pack="fas" icon="info-circle" size="is-small" type="is-warning"></b-icon>
            {{ $t('project_dates_not_set') }}
          </div>

        </div>
      </div>

    </div>

    <!-- Add holiday modal -->
    <b-modal v-model="showAddModal" has-modal-card :can-cancel="['escape', 'outside']" trap-focus>
      <div class="modal-card rounded-xl overflow-hidden">
        <header class="modal-card-head">
          <p class="modal-card-title">
            <b-icon pack="fas" icon="calendar-plus" class="mr-2" type="is-primary"></b-icon>
            {{ $t('add_holiday') }}
          </p>
          <button class="delete" @click="showAddModal = false"></button>
        </header>
        <section class="modal-card-body">
          <b-field :label="$t('date')">
            <b-input :value="newHoliday.dateFormatted" disabled icon-pack="fas" icon="calendar"></b-input>
          </b-field>
          <b-field :label="$t('holiday_name')" :type="nameError ? 'is-danger' : ''" :message="nameError">
            <b-input v-model="newHoliday.name" :placeholder="$t('holiday_name_placeholder')"
              icon-pack="fas" icon="tag" ref="holidayNameInput"
              @keyup.enter.native="confirmAddHoliday"></b-input>
          </b-field>
        </section>
        <footer class="modal-card-foot modal-card-foot--right">
          <b-button @click="showAddModal = false">{{ $t('cancel') }}</b-button>
          <b-button type="is-primary" icon-pack="fas" icon-left="calendar-plus"
            :loading="savingHoliday" @click="confirmAddHoliday">
            {{ $t('add_holiday') }}
          </b-button>
        </footer>
      </div>
    </b-modal>

  </div>
</template>

<script>
import axios from 'axios'
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import ScaleLoader from 'vue-spinner/src/ScaleLoader.vue'

export default {
  name: 'project-calendar',

  components: { FullCalendar, ScaleLoader },

  props: {
    projectId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      isLoading: false,
      savingWorkingDays: false,
      savingHoliday: false,
      projectStartDate: null,
      projectEndDate: null,
      workingDays: [1, 2, 3, 4, 5],
      calendarEvents: [],
      holidays: [],
      showAddModal: false,
      newHoliday: { date: '', dateFormatted: '', name: '' },
      nameError: '',
    }
  },

  computed: {
    calendarOptions() {
      const self = this
      return {
        plugins: [dayGridPlugin, interactionPlugin],
        initialView: 'dayGridMonth',
        locale: 'es',
        firstDay: 1,
        weekends: true,
        dayMaxEvents: true,
        editable: false,
        selectable: false,
        height: 'auto',
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,dayGridWeek',
        },
        buttonText: { today: 'Hoy', month: 'Mes', week: 'Semana' },
        events: self.calendarEvents,
        validRange: self.computedValidRange,
        dateClick(info) { self.handleDateClick(info) },
        eventClick(info) { self.handleEventClick(info) },
        dayCellClassNames(info) { return self.getDayCellClasses(info) },
      }
    },

    computedValidRange() {
      if (!this.projectStartDate || !this.projectEndDate) return undefined
      return {
        start: this.shiftDate(this.projectStartDate, -1),
        end: this.shiftDate(this.projectEndDate, 2), // +2 porque end es exclusivo en FullCalendar
      }
    },

    weekDays() {
      const labels = this.$t('weekdays').split('_')
      const abbrs = this.$t('weekdaysMin').split('_')
      return [
        { value: 1, label: labels[1], abbr: abbrs[1] },
        { value: 2, label: labels[2], abbr: abbrs[2] },
        { value: 3, label: labels[3], abbr: abbrs[3] },
        { value: 4, label: labels[4], abbr: abbrs[4] },
        { value: 5, label: labels[5], abbr: abbrs[5] },
        { value: 6, label: labels[6], abbr: abbrs[6] },
        { value: 0, label: labels[0], abbr: abbrs[0] },
      ]
    },

    holidaysSorted() {
      return [...this.holidays].sort((a, b) => a.date.localeCompare(b.date))
    },
  },

  async created() {
    await this.loadProjectDates()
    await this.loadCalendarData()
  },

  mounted() {
    this.$nextTick(() => {
      if (this.$refs.fullCalendar) this.$refs.fullCalendar.getApi().updateSize()
    })
  },

  methods: {

    async loadProjectDates() {
      this.isLoading = true
      try {
        const { data } = await axios.get(`/api/v2/project/${this.projectId}`)
        const project = this.extractProject(data)
        if (project) {
          this.projectStartDate = project.current_start_date
          this.projectEndDate = project.current_end_date
          if (this.projectStartDate) {
            this.$nextTick(() => {
              if (this.$refs.fullCalendar) {
                this.$refs.fullCalendar.getApi().gotoDate(this.projectStartDate)
              }
            })
          }
        }
      } catch (e) {
        console.error('Error loading project dates', e)
      } finally {
        this.isLoading = false
      }
    },

    async loadCalendarData() {
      try {
        const { data } = await axios.get(`/api/v2/project/${this.projectId}/calendar`)
        if (data && data.success) {
          // API devuelve { sunday: false, monday: true, ... } → convertir a array de enteros para FullCalendar
          this.workingDays = this.weekDaysObjectToArray(data.data.work_days)
          const holidays = data.data.holidays.map(h => ({ ...h, date: h.date_day }))
          this.holidays = holidays
          this.calendarEvents = holidays.map(this.mapHolidayToEvent)
        }
      } catch (e) {
        console.error('Error loading calendar data', e)
      }
    },

    extractProject(data) {
      if (!data) return null
      if (data.current_start_date !== undefined) return data
      if (data.data && data.data.current_start_date !== undefined) return data.data
      if (data.project) return data.project
      return null
    },

    toggleDay(value) {
      const idx = this.workingDays.indexOf(value)
      this.workingDays = idx === -1
        ? [...this.workingDays, value]
        : this.workingDays.filter(d => d !== value)
    },

    async saveWorkingDays() {
      this.savingWorkingDays = true
      try {
        await axios.put(`/api/v2/project/${this.projectId}/calendar/week-days`, {
          week_days: this.weekDaysArrayToObject(this.workingDays),
        })
        this.$notify.success('saved_successfully')
      } catch (e) {
        this.$notify.error('error_saving_data')
      } finally {
        this.savingWorkingDays = false
      }
    },

    getDayCellClasses(info) {
      return this.workingDays.includes(info.date.getDay()) ? [] : ['fc-day-nonworking']
    },

    handleDateClick(info) {
      if (this.calendarEvents.some(e => e.date === info.dateStr)) return
      this.newHoliday = { date: info.dateStr, dateFormatted: this.formatDate(info.dateStr), name: '' }
      this.nameError = ''
      this.showAddModal = true
      this.$nextTick(() => {
        setTimeout(() => { if (this.$refs.holidayNameInput) this.$refs.holidayNameInput.focus() }, 200)
      })
    },

    async confirmAddHoliday() {
      if (!this.newHoliday.name.trim()) {
        this.nameError = this.$t('field_required')
        return
      }
      this.nameError = ''
      this.savingHoliday = true
      try {
        const { data } = await axios.post(`/api/v2/project/${this.projectId}/calendar/holidays`, {
          name: this.newHoliday.name.trim(),
          date_day: this.newHoliday.date,
        })
        if (data && data.success) {
          this.addHolidayLocally({ ...data.data, date: data.data.date_day })
          this.showAddModal = false
        }
      } catch (e) {
        this.$notify.error('error_saving_data')
      } finally {
        this.savingHoliday = false
      }
    },

    handleEventClick(clickInfo) {
      this.$dialog.confirm({
        title: this.$t('delete_holiday'),
        message: this.$t('confirm_delete_holiday', [clickInfo.event.title]),
        confirmText: this.$t('delete'),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.removeHoliday(clickInfo.event.id),
      })
    },

    deleteHolidayFromList(holiday) {
      this.$dialog.confirm({
        title: this.$t('delete_holiday'),
        message: this.$t('confirm_delete_holiday', [holiday.name]),
        confirmText: this.$t('delete'),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.removeHoliday(holiday.id),
      })
    },

    async removeHoliday(holidayId) {
      try {
        await axios.delete(`/api/v2/project/${this.projectId}/calendar/holidays/${holidayId}`)
        this.holidays = this.holidays.filter(h => h.id != holidayId)
        this.calendarEvents = this.calendarEvents.filter(e => e.id != holidayId)
      } catch (e) {
        this.$notify.error('error_deleting_data')
      }
    },

    addHolidayLocally(holiday) {
      this.holidays = [...this.holidays, holiday]
      this.calendarEvents = [...this.calendarEvents, this.mapHolidayToEvent(holiday)]
    },

    // Convierte { sunday: false, monday: true, ... } → [1, 2, 3, 4, 5] (índices JS getDay())
    weekDaysObjectToArray(obj) {
      const order = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
      return order.reduce((acc, name, i) => { if (obj[name]) acc.push(i); return acc }, [])
    },

    // Convierte [1, 2, 3, 4, 5] → { sunday: false, monday: true, ... }
    weekDaysArrayToObject(arr) {
      const order = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
      return order.reduce((acc, name, i) => { acc[name] = arr.includes(i); return acc }, {})
    },

    mapHolidayToEvent(holiday) {
      return {
        id: holiday.id,
        title: holiday.name,
        date: holiday.date,
        backgroundColor: '#e05252',
        borderColor: '#c53030',
        textColor: '#ffffff',
        classNames: ['fc-holiday-event'],
        extendedProps: { isHoliday: true },
      }
    },

    shiftDate(dateStr, days) {
      const d = new Date(dateStr + 'T00:00:00')
      d.setDate(d.getDate() + days)
      return d.toISOString().split('T')[0]
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const [y, m, d] = dateStr.split('-')
      return `${d}/${m}/${y}`
    },
  },
}
</script>

<style scoped>
.modal-card-foot--right { justify-content: flex-end; }
.day-label { border-color: #d1d5db; background: #f9fafb; }
.day-label:hover { border-color: #93c5fd; background: #eff6ff; }
.day-label.is-active { border-color: #5580b5; background: #5580b5; color: #fff; }
</style>

<style>
.fc-day-nonworking { background-color: #fff8f0 !important; }
.fc-holiday-event { cursor: pointer; font-weight: 600; }
.fc-holiday-event:hover { opacity: 0.85; }
.fc-daygrid-day-frame { cursor: pointer; }
.fc-toolbar-title { font-size: 1.15em !important; font-weight: 700; color: #3d5a8a; text-transform: uppercase; }
.fc-day-today { background-color: #eef3fb !important; }
.fc-button-primary { background-color: #5580b5 !important; border-color: #5580b5 !important; }
.fc-button-primary:hover { background-color: #3d5a8a !important; border-color: #3d5a8a !important; }
.fc-button-primary:not(:disabled).fc-button-active { background-color: #3d5a8a !important; border-color: #3d5a8a !important; }
</style>
